import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { wait } from '../../lib/wait';
import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { VariableUtils } from '../../store/logic/VariableUtils';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useApi } from '../../store/SelectedHomeyStore.hooks';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';

import { CloseButton } from '../../components/buttons/CloseButton';
import { SolidButton } from '../../components/buttons/SolidButton';
import { Heading3Medium } from '../../components/common/text/Heading3Medium';
import { RadioGroup } from '../../components/forms/radio/RadioGroup';
import { TypeRadio } from '../../components/forms/radio/TypeRadio';
import { SelectControllable } from '../../components/forms/select/SelectControllable';
import { TextField } from '../../components/forms/TextField';
import { Dialog } from '../../components/overlay/Dialog';

export function LogicDialog() {
  const { i18n } = useI18n();
  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    control,
  } = useForm({
    defaultValues: {
      type: 'string',
      name: '',
      value: '',
    },
  });

  const { api } = useApi();

  function onSubmit(data) {
    setIsSaving(true);
    const values = { ...data };
    values.value = VariableUtils.parseValue(values.type, values.value);

    const waitPromise = wait(500);
    const setPromise = api.logic.createVariable({ variable: values });

    // todo fix catch and then it makes no sense
    Promise.all([waitPromise, setPromise])
      .then(([, result]) => {
        ToastManager.add({
          icon: glyph.system.logic,
          message: i18n.messageFormatter(`logic.createdVariable`, {
            name: result.name,
          }),
        });
        setIsSaving(false);
        handleClose();
      })
      .catch((error) => {
        ToastManager.handleError(error);
        setIsSaving(false);
      });
  }

  function handleClose() {
    RouteManager.pushState({});
  }

  // eslint-disable-next-line react-hooks/incompatible-library
  const watchType = watch('type');

  useEffect(() => {
    if (watchType === 'boolean') {
      reset({
        type: 'boolean',
        name: '',
        value: 'true',
      });
    } else {
      reset({
        type: watchType,
        name: '',
        value: '',
      });
    }
  }, [watchType, reset]);

  return (
    <Dialog onClose={handleClose} isOpen>
      <S.Root>
        <S.Header>
          <Heading3Medium>{i18n.messageFormatter(`logic.newVariable`)}</Heading3Medium>

          <CloseButton onPress={handleClose} />
        </S.Header>

        <S.Form id="logic-dialog-form" onSubmit={handleSubmit(onSubmit)}>
          <RadioGroup
            name="type"
            aria-label={i18n.messageFormatter(`logic.type`)}
            orientation="horizontal"
            defaultValue="string"
            register={register}
          >
            <TypeRadio type="string" value="string">
              {i18n.messageFormatter(`logic.string`)}
            </TypeRadio>
            <TypeRadio type="number" value="number">
              {i18n.messageFormatter(`logic.number`)}
            </TypeRadio>
            <TypeRadio type="boolean" value="boolean">
              {i18n.messageFormatter(`logic.boolean`)}
            </TypeRadio>
          </RadioGroup>

          <TextField
            autoComplete="off"
            orientation="vertical"
            type="text"
            name="name"
            label={i18n.messageFormatter('common.name')}
            placeholder={i18n.messageFormatter('logic.namePlaceholder')}
            register={register}
            required
            minLength={1}
            error={errors.name}
          />

          {watchType === 'boolean' ? (
            <SelectControllable
              orientation="vertical"
              name="value"
              label={i18n.messageFormatter('common.value')}
              placeholder={i18n.messageFormatter('common.value')}
              defaultValue="true"
              options={[
                { key: 'true', textValue: i18n.messageFormatter('common.true') },
                { key: 'false', textValue: i18n.messageFormatter('common.false') },
              ]}
              required
              error={errors.value}
              control={control}
            />
          ) : (
            <TextField
              autoComplete="off"
              orientation="vertical"
              type={watchType === 'number' ? 'number' : 'text'}
              step={watchType === 'number' ? 'any' : undefined}
              name="value"
              label={i18n.messageFormatter('common.value')}
              placeholder={i18n.messageFormatter(`logic.${watchType}Placeholder`)}
              register={register}
              required
              minLength={1}
              error={errors.value}
            />
          )}
        </S.Form>

        <S.Footer>
          <SolidButton
            form="logic-dialog-form"
            type="submit"
            styleFlat={true}
            styleWidth="full"
            styleColor="blue"
            isDisabled={isSaving}
          >
            <SolidButton.Text>{i18n.messageFormatter('common.create')}</SolidButton.Text>
          </SolidButton>
        </S.Footer>
      </S.Root>
    </Dialog>
  );
}

function S() {}
LogicDialog.S = S;

S.Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 320px;
  max-width: 320px;
`;

S.Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${su(2, 2, 0, 2)};
`;

S.Form = styled.form`
  padding: ${su(2, 2, 1, 2)};

  ${TextField.Root}, ${SelectControllable.S.Root} {
    margin: ${su(2)} 0;
  }
`;

S.Footer = styled.footer`
  padding: ${su(0, 1, 1, 1)};
`;
